import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useGetTransportAxiosInstance } from "@/constants";
import type {
    CreateHotelClinicTaskRequestPayload,
    HotelClinicCreateTaskPassengerPayload,
    Req_CreateHotelClinicTasksInBulk,
} from "@/pages/HotelPlanning/clinic-arrival/utils/hotelClinicCreateTaskPayload";
import type { Req_UpdateHotelClinicRequestsInBulkPayload } from "@/requests/hotel-clinic-request-update.types";
import type { Req_UpdateHotelClinicTasksInBulkPayload } from "@/requests/hotel-clinic-task-update.types";
import { getHotelClinicManagerTasksByDateQueryKey } from "@/requests/hotel-clinic.queries";
import type { Req_NotificationTemplatesPayload } from "@/requests/notification-templates.types";
import { type ICommonListResponse, commonListResponseSchema } from "@/schemas/commonList";
import { handleNetworkError } from "@/utils/handleNetworkError";

export const CREATE_HOTEL_CLINIC_TASK_MUTATION_KEY = ["createHotelClinicTask"] as const;

export function useCreateHotelClinicTaskMutation() {
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation({
        mutationKey: CREATE_HOTEL_CLINIC_TASK_MUTATION_KEY,
        mutationFn: async (payload: CreateHotelClinicTaskRequestPayload) => {
            const { data } = await handleNetworkError(axiosInstance.post("/hotel-clinic/tasks", payload));
            return data;
        },
    });
}

export const CREATE_HOTEL_CLINIC_TASKS_BULK_MUTATION_KEY = ["createHotelClinicTasksBulk"] as const;

/**
 * `POST /hotel-clinic/tasks/bulk` — one flat, driver-tagged passenger list; backend groups them
 * into tasks and merges into existing PENDING ones. Returns a per-passenger result (partial
 * success); query invalidation is left to the caller so it can clear succeeded rows in sequence.
 */
export function useCreateHotelClinicTasksInBulkMutation() {
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<ICommonListResponse, Error, Req_CreateHotelClinicTasksInBulk>({
        mutationKey: CREATE_HOTEL_CLINIC_TASKS_BULK_MUTATION_KEY,
        mutationFn: async (payload) => {
            const { data } = await handleNetworkError(axiosInstance.post("/hotel-clinic/tasks/bulk", payload));
            const parsedData = commonListResponseSchema.safeParse(data);
            if (!parsedData.success) {
                console.error(parsedData.error);
                throw new Error(parsedData.error.message);
            }
            return parsedData.data;
        },
    });
}

export function useUpdateHotelClinicTasksMutation({ isoDate }: { isoDate: string | undefined }) {
    const axiosInstance = useGetTransportAxiosInstance();
    const queryClient = useQueryClient();

    return useMutation<ICommonListResponse, Error, Req_UpdateHotelClinicTasksInBulkPayload>({
        mutationFn: async (payload: Req_UpdateHotelClinicTasksInBulkPayload) => {
            const { data } = await handleNetworkError(axiosInstance.patch("/hotel-clinic/tasks", payload));
            const parsedData = commonListResponseSchema.safeParse(data);
            if (!parsedData.success) {
                console.error(parsedData.error);
                throw new Error(parsedData.error.message);
            }
            return parsedData.data;
        },
        onSuccess: () => {
            if (isoDate) {
                queryClient.invalidateQueries({ queryKey: getHotelClinicManagerTasksByDateQueryKey(isoDate) });
            }
        },
    });
}

/**
 * `PATCH /hotel-clinic-requests` — labels the passenger request itself (not the task), so it
 * works whether or not a driver/task has been assigned yet. Used by the hotel-departure
 * custom-passenger `labelledUser` flow.
 */
export function useUpdateHotelClinicRequestsLabelMutation() {
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<ICommonListResponse, Error, Req_UpdateHotelClinicRequestsInBulkPayload>({
        mutationFn: async (payload) => {
            const { data } = await handleNetworkError(axiosInstance.patch("/hotel-clinic-requests", payload));
            const parsedData = commonListResponseSchema.safeParse(data);
            if (!parsedData.success) {
                console.error(parsedData.error);
                throw new Error(parsedData.error.message);
            }
            return parsedData.data;
        },
    });
}

/**
 * `POST /notification-templates` — notifies the affected driver(s) after a label assign/unassign.
 * Batches every row touched in one save into a single call; caller filters to rows whose label
 * PATCH actually succeeded before building this payload.
 */
export function useSendNotificationTemplatesMutation() {
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<ICommonListResponse, Error, Req_NotificationTemplatesPayload>({
        mutationFn: async (payload) => {
            const { data } = await handleNetworkError(axiosInstance.post("/notification-templates", payload));
            const parsedData = commonListResponseSchema.safeParse(data);
            if (!parsedData.success) {
                console.error(parsedData.error);
                throw new Error(parsedData.error.message);
            }
            return parsedData.data;
        },
    });
}

/** PATCH `/hotel-clinic/tasks/:taskId/assign` — passenger shape matches POST create-task. */
export function useAssignHotelClinicTaskPassengersMutation() {
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<unknown, Error, { taskId: string; passengers: HotelClinicCreateTaskPassengerPayload[] }>({
        mutationFn: async ({ taskId, passengers }) => {
            const { data } = await handleNetworkError(
                axiosInstance.patch(`/hotel-clinic/tasks/${taskId}/assign`, { passengers })
            );
            return data;
        },
    });
}

export function useUnassignHotelClinicTaskPassengersMutation({
    isoDate,
    invalidateQuery = true,
}: {
    isoDate: string;
    invalidateQuery?: boolean;
}) {
    const axiosInstance = useGetTransportAxiosInstance();
    const queryClient = useQueryClient();

    return useMutation<unknown, Error, { taskId: string; passengers: string[] }>({
        mutationFn: async ({ taskId, passengers }) => {
            const { data } = await handleNetworkError(
                axiosInstance.patch(`/hotel-clinic/tasks/${taskId}/unassign`, { passengers })
            );
            return data;
        },
        onSuccess: () => {
            if (invalidateQuery) {
                const queryKey = getHotelClinicManagerTasksByDateQueryKey(isoDate);
                queryClient.invalidateQueries({ queryKey });
            }
        },
    });
}
